'use client';

import { useEffect, useMemo } from 'react';
import { useStore } from '@/store/store';
import { BRAND, COUNTRY_ABBREVIATION } from '@/utils/constants';
import { getConfig } from '@/utils/config/config';
import { getAppPlatform } from '@/logic/platform';
import { buildPokerTournamentTicketFullUrl } from '@fanduel/rewards-hub-client/promotion-utils';
import type { RewardsConfig } from '@fanduel/rewards-hub-client/provider';
import type { RewardsPageConfig } from '@/logic/page-config/rewards/rewards-page-config';
import { getCountryCode } from '@/utils/region/get-region';
import { buildAccountUrl } from '@/utils/url/account';
import { buildApiUrl } from '@/utils/url/build-url';
import { useGlobalContext } from '@/store/contexts/global-context';
import { REFERRER_PLACEHOLDER } from './constants';
import { getRewardsApiUrlFromAppConfig } from '@/logic/rewards-hub/utils/get-rewards-api-url';
import { rewardsHubRequest } from '@/logic/rewards-hub/rewards-hub-request';
import { PokerTournamentTicketGameLauncherWrapper } from '@/app/components/game-launcher/poker-tournament-ticket-game-launcher.wrapper';
import { ddRumError } from '@/utils/datadog';
import { ddErrorPokerTournamentTicketGameIdMissing } from '@/utils/datadog/poker';

const POKER_TOURNAMENT_TICKET_UID = 'poker-tournament-ticket';

type PrismicLinkedItem = { id?: string; uid?: string };
type ZoneItemBodyEntryItem = { item?: PrismicLinkedItem };
type ZoneItemBodyEntry = { items?: ZoneItemBodyEntryItem[] };
type ZoneItem = { data?: { body?: ZoneItemBodyEntry[] } };
type PageBodySlice = { primary?: { zone_item?: ZoneItem } };

const findPokerTicketInZoneBody = (innerBody: ZoneItemBodyEntry[]): PrismicLinkedItem | undefined => {
    for (const entry of innerBody) {
        const items = entry?.items;
        if (!Array.isArray(items)) {
            continue;
        }
        for (const wrapper of items) {
            const linked = wrapper?.item;
            if (linked?.uid === POKER_TOURNAMENT_TICKET_UID) {
                return linked;
            }
        }
    }
    return undefined;
};

const findPokerTournamentTicketZoneItem = (pageConfig?: RewardsPageConfig): PrismicLinkedItem | undefined => {
    const slices = pageConfig?.body as PageBodySlice[] | undefined;
    if (!Array.isArray(slices)) {
        return undefined;
    }
    for (const slice of slices) {
        const innerBody = slice?.primary?.zone_item?.data?.body;
        if (!Array.isArray(innerBody)) {
            continue;
        }
        const found = findPokerTicketInZoneBody(innerBody);
        if (found) {
            return found;
        }
    }
    return undefined;
};

type Route = {
    href: string;
    label?: string;
};

export type RouteConfig = {
    routes: {
        home: Route;
        login: Route;
        join: Route;
        promotions: Route;
        view_promotion: Route;
        leaderboard: Route;
        customLogin: Route;
        customJoin: Route;
    };
};

export type PromoConfig = {
    routeConfig: RouteConfig;
    location: { value: string };
    themeOverrides?: { productTheme: string };
    isLoggedIn: boolean;
};

export const useCppRoutes = (page = 'rewards', title = 'Rewards'): RouteConfig => {
    const { region } = useGlobalContext();
    const domain = typeof window !== 'undefined' ? window.location.origin : '';

    const login = buildAccountUrl('/login', region);
    const register = buildAccountUrl('/register', region);

    return useMemo(
        () => ({
            routes: {
                login: { label: 'Login', href: `${login}?external-referrer-next=${page}` },
                join: { label: 'Register', href: `${register}?external-referrer-next=${page}` },
                customLogin: { label: 'Login', href: `${login}?external-referrer-next=${REFERRER_PLACEHOLDER}` },
                customJoin: { label: 'Register', href: `${register}?external-referrer-next=${REFERRER_PLACEHOLDER}` },
                home: { label: 'Home', href: `${domain}` },
                promotions: { label: `${title}`, href: `${domain}/${page}` },
                view_promotion: { label: `${title} Details`, href: `${domain}/${page}/:promoCode` },
                leaderboard: { label: 'Leaderboard', href: `${domain}/${page}/:promoCode/leaderboard` }
            }
        }),
        [domain, login, register, page, title]
    );
};

export const useCppConfig = (route = 'rewards'): [PromoConfig] => {
    const { region } = useGlobalContext();
    const isLoggedIn = useStore((s) => s.userContext.isLoggedIn);
    const themeVariant = useStore((s) => s.theme.variant);

    const routeConfig = useCppRoutes(route);

    const countryCode = getCountryCode();

    return useMemo(
        () => [
            {
                routeConfig,
                location: { value: `${countryCode}-${region}` },
                themeOverrides: { productTheme: themeVariant },
                isLoggedIn
            }
        ],
        [routeConfig, countryCode, region, themeVariant, isLoggedIn]
    );
};

export const useRewardsHubConfig = (pageProduct?: RewardsPageConfig, isLoggedIn: boolean = false): RewardsConfig => {
    const config = getConfig();
    const { region, isMobile, isAndroidWrapper, isAndroid, isIosWrapper, isIos } = useGlobalContext();
    const showRegionSelector = useStore((s) => s.regionInfo.showRegionSelector);
    const userId = useStore((s) => s.userContext?.accountSettings?.accountID);

    const productType = process.env.PRODUCT as RewardsConfig['product'];
    const brand = process.env.BRAND as RewardsConfig['brand'];
    const country = process.env.COUNTRY as RewardsConfig['country'];

    const countryCodeKey = (country || '').toUpperCase() as keyof typeof COUNTRY_ABBREVIATION;
    const countryCode = COUNTRY_ABBREVIATION[countryCodeKey];

    const baseAccount = (path: string) => buildAccountUrl(path, region);

    const pphBrand = ((): RewardsConfig['pphBrand'] => {
        if (brand === 'fanduel') {
            return country === 'canada' ? 'FANDUEL_CANADA' : 'FANDUEL';
        }
        return 'FANDUEL';
    })();

    const footerBody = pageProduct?.footer?.data?.body || [];
    const footerItems = footerBody?.find((item) => item?.primary?.category_title === 'Help');
    const supportUrl = footerItems?.items?.find((item) => item?.link_text === 'Support')?.link_url?.url ?? '';

    const features = pageProduct?.features || [];

    const { setHideBottomBar } = useStore((state) => state.uiToggles);

    const pokerTicket = findPokerTournamentTicketZoneItem(pageProduct);
    const pokerTournamentTicketGameId = pokerTicket?.id;
    const pokerTournamentTicketGameUid = pokerTicket?.uid;

    useEffect(() => {
        // The zone item was resolved from aggregation (so the CMS wiring for this
        // page is intact) but its linked Prismic document has no `id` — this is the
        // one state that guarantees the launcher will be hit with `gameId=undefined`
        // (UAG-001) for every ticket click on this page.
        if (!pokerTicket || pokerTournamentTicketGameId) {
            return;
        }
        ddRumError(
            ddErrorPokerTournamentTicketGameIdMissing({
                region: region || '',
                brand: brand || BRAND.FANDUEL,
                userId,
                ticketUid: pokerTournamentTicketGameUid,
                ticketId: pokerTournamentTicketGameId,
                sliceCount: pageProduct?.body?.length ?? 0,
                pageProduct: JSON.stringify(pageProduct)
            })
        );
    }, [pokerTicket, pokerTournamentTicketGameId, pokerTournamentTicketGameUid, region, brand, userId, pageProduct]);

    const gameLaunchBaseUrl = config.apis?.LauncherClient ? buildApiUrl(config.apis.LauncherClient) : undefined;

    const pokerTournamentTicketLaunchUrl =
        pokerTicket && gameLaunchBaseUrl
            ? buildPokerTournamentTicketFullUrl(
                  gameLaunchBaseUrl,
                  region || '',
                  String(brand || 'fanduel').toLowerCase(),
                  {
                      gameId: pokerTournamentTicketGameId,
                      gameUid: pokerTournamentTicketGameUid
                  }
              )
            : undefined;

    return {
        product: productType,
        region,
        brand,
        showRegionSelector,
        balanceData: { playableBalance: 0 },
        location: `${countryCode}-${region}`,
        subDomain: config.subDomain,
        footerBody,
        features,
        isLoggedIn,
        loyaltyTermsLoginUrl: `${baseAccount('/login')}?external-referrer-next=loyalty-terms`,
        loyaltyDetailsLoginUrl: `${baseAccount('/login')}?external-referrer-next=loyalty-details`,
        rewardsLoginUrl: `${baseAccount('/login')}?external-referrer-next=rewards`,
        bonusSpinsDetailsLoginUrl: `${baseAccount('/login')}?external-referrer-next=bonus-spins-details`,
        registerUrl: `${baseAccount('/register')}?external-referrer-next=rewards`,
        country,
        rewardsApiUrl: getRewardsApiUrlFromAppConfig(config),
        ...(userId ? { userId } : {}),
        // Route rewards-hub-client HTTP through casino-next's interceptor
        // pipeline (credentials, PX, internal-network, and — in mock builds
        // only — the mockWorkerInterceptor that injects `x-mock-worker-id`
        // for parallel mock-tests). `mockWorkerInterceptor` is the only
        // interceptor that is conditionally registered; the others always run.
        fetch: rewardsHubRequest,
        rewardsPagePath: '/rewards',
        loyaltyDetailsPath: '/loyalty-details',
        loyaltyDetailsRewardsPath: '/loyalty-details#rewards',
        bonusSpinsDetailsPath: '/bonus-spins-details',
        rewardsHubUrl: '/rewards',
        termsUrl: '/loyalty-terms',
        supportUrl,
        pphApi: config.apis?.CppClient?.baseUrl || '',
        pphBrand,
        isMobile,
        isAndroidWrapper,
        isAndroid,
        isIosWrapper,
        isIos,
        platform: getAppPlatform(),
        setHideBottomBar,
        ...(pokerTicket
            ? {
                  pokerTournamentTicketGameId,
                  pokerTournamentTicketGameUid,
                  ...(pokerTournamentTicketLaunchUrl
                      ? {
                            pokerTournamentTicketLaunchUrl,
                            pokerTournamentTicketGameLauncherWrapper: PokerTournamentTicketGameLauncherWrapper
                        }
                      : {})
              }
            : {})
    };
};
